import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { compose } from 'recompose';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import GridLayout from 'react-grid-layout';
import AddWidget from '../../components/Dashboard/AddWidget';
import { ContextMenu, MenuItem } from 'react-contextmenu';
import Widget from '../../components/Dashboard/Widget';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import * as entities from '../../graphql/entities';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import ActionButtons from '../../components/connectedForm/ActionButtons';
import ViewController from '../../components/view/ViewController';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import Dropdown from '../../components/connectedForm/Dropdown';
import { PrimaryButton } from '../../components/connectedForm/Button';
import { serviceActions } from '../../state/modules/service';
import setDocumentTitle from '../../utils/SetDocumentTitle';
import { ViewContextProvider } from '../../components/Context/ViewContext';
import { AlertType } from '../../graphql/schema/p4types';
import * as withContainer from '../../graphql/containers';
import { cloneDeep } from '@apollo/client/utilities';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import FluentModal from '../../components/modals/FluentModal';

const ContentContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${p4Tokens.colorHeaderBackground};
  padding: 12px 0 73px;
`;

const DashboardTitle = styled.span`
  font-weight: 500;
  color: ${p4Tokens.fieldLabel};
`;

const GridContainer = styled.div`
  padding: 0px 16px 100px 16px;
  height: 100%;
  overflow: auto;
`;

const DashboardViewConfig = ({ shared }) => {
  const sharedWith = shared ? (
    <Form.Field
      dataid={'sharedWith'}
      component={Dropdown}
      label={'Shared With'}
      multi={true}
      initialized={true}
      {...entities.CompanyUserRole.dropdown}
    />
  ) : null;

  return (
    <>
      <Form.Legend label={'Properties'} />
      <Form.Field dataid={`name`} component={BlurTextField} label={'Name'} />
      <Form.Field dataid={`shared`} component={CheckboxInput} label={'Shared'} />
      {sharedWith}
    </>
  );
};

const DashboardHeaderBlock = ({ values, readOnly, initialize, onSubmit, actionButtons }) => {
  const appUser = useSelector((store) => store.app.user);
  const dispatch = useDispatch();
  const dashboardListVariables = useRef({ screen: '/' });

  const { data, refetch } = useQuery(entities.Dashboard.dropdown.query, { variables: dashboardListVariables.current });
  useEffect(() => {
    refetch();
  }, [values]);

  if (!values) return null;

  const dashboards = data?.dashboards?.edges?.map?.((a) => a.node);
  const currentUserId = app?.fullUserDetails?.companyUser?.id;
  const views = { view: dashboards || [], currentView: {} };

  const onDelete = (description) => {
    if (description === 'Default') {
      dispatch(serviceActions.genericAlert('Dashboard reset', 'Dashboard has been reset', AlertType.Info));
    } else {
      dispatch(serviceActions.genericAlert('Dashboard deleted', 'Dashboard has been removed', AlertType.Info));
    }
    refetch();
  };

  const { name, shared } = values;

  return (
    <HeaderContainer>
      <HeaderTitle
        value={
          <DashboardTitle>
            Dashboard
            {name ? ` - ${name}` : null}
          </DashboardTitle>
        }
        optValue={null}
      />
      <HeaderAction>
        <ViewContextProvider value={{ viewScreenName: '/' }}>
          <ViewController
            dashboardViews={views}
            user={appUser}
            screenName={'/'}
            embeddedView={true}
            initialize={initialize}
            type={'DASHBOARD'}
            viewName={name || null}
            onSave={() => onSubmit() /*Wrapper to make sure we pass empty params*/}
            currentUserId={currentUserId}
            customViewConfigComponent={<DashboardViewConfig shared={shared} />}
            onDeleteComplete={onDelete}
            currentView={values}
            readOnly={readOnly}
          />
        </ViewContextProvider>
        <ActionButtons actionButtons={actionButtons} values={values} />
      </HeaderAction>
    </HeaderContainer>
  );
};

const setColumns = (gridWidth) => {
  let cols = 2;
  if (gridWidth > 1860) {
    cols = 16;
  } else if (gridWidth > 1480) {
    cols = 12;
  } else if (gridWidth > 980) {
    cols = 8;
  } else if (gridWidth > 736) {
    cols = 4;
  }
  return cols;
};

const DashboardForm = (props) => {
  const [addMode, setAddMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [gridMetrics, setGridMetrics] = useState(null);
  const [selectedUUID, setSelectedUUID] = useState(null);

  const app = useSelector((store) => store.app);
  const dispatch = useDispatch();

  const onResize = useCallback(() => {
    const gridWidth = app?.appMenuHidden ? window.innerWidth - 40 : window.innerWidth - 250;
    const cols = setColumns(gridWidth);

    const cardWidth = Math.round(gridWidth / cols);
    const rowHeight = Math.round((cardWidth - 20) / 2);
    setGridMetrics({
      gridWidth,
      cardWidth,
      rowHeight,
      cols,
    });
  }, []);

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize, false);
    setDocumentTitle(`Dashboard`);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const addWidget = () => {
    setAddMode(true);
    setSelectedUUID(null);
  };

  const hideModal = () => {
    setAddMode(false);
    setEditMode(false);
  };

  const updateLayout = (newLayout) => {
    const dashboard = props.values;
    if (!dashboard || !dashboard.widgets) return;

    const newWidgets = newLayout.map((widgetLayout, newIndex) => {
      const { x, y, h, w, i } = widgetLayout;
      const newWidget = { ...dashboard.widgets[i] };
      newWidget.x = x;
      newWidget.y = y;
      newWidget.h = h;
      newWidget.w = w;
      return newWidget;
    });

    dispatch(props.change('widgets', newWidgets));
    //TODO - Resolve issues with doing this.
    // - Some screens are opened in 'modal' mode, which leaves another screen active behind them (So it's quick to return to I think?)
    // - If that screen behind is the dashboard, it triggers a layout update, which triggers this submit & causes unexpected navigation.
    // - Not sure how to resolve right now so disabling it for the moment.
    // connectedForm.submit();
  };

  const deleteWidget = (idx) => {
    dispatch(props.change(`widgets.${idx}.deleted`, true));
  };

  //TODO - Own Component
  const renderWidgetButtons = (widgetData, idx) => {
    const renderedButtons = [];

    renderedButtons.push(
      <MenuItem
        key={'actionButtonEdit'}
        onClick={(e) => {
          e.preventDefault();
          setEditMode(true);
          setSelectedUUID(widgetData.uuid);
        }}
      >
        Edit
      </MenuItem>
    );
    renderedButtons.push(
      <MenuItem
        key={'actionButtonDelete'}
        onClick={(e) => {
          e.preventDefault();
          deleteWidget(idx);
        }}
      >
        Remove
      </MenuItem>
    );

    return renderedButtons;
  };

  //TODO - Own component
  const renderWidgets = (dashLayout) => {
    const { cols } = gridMetrics;
    const widgets = [];
    const menus = [];
    const gridLayout = [];
    if (Array.isArray(dashLayout) && dashLayout.length > 0) {
      dashLayout.forEach((item, idx) => {
        const className = item.type === 'WORKSPACE' ? 'miniGrid' : null;

        const widget = (
          <div key={idx} className={className}>
            <Form.Field
              idx={idx}
              dataid={`widgets.${idx}`}
              component={Widget}
              gridMetrics={gridMetrics}
              app={app}
              data-testid={`dashboard-widget-${item.label}-${item.type}`.replaceAll(' ', '-').toLowerCase()}
            />
          </div>
        );
        if (widget) {
          const menu = (
            <ContextMenu key={`menu_${idx}`} id={`widget_menu_${idx}`}>
              {renderWidgetButtons(item, idx)}
            </ContextMenu>
          );
          widgets.push(widget);
          menus.push(menu);

          let x = item.x;
          if (item.x + item.w > cols) {
            x = cols - item.w > 0 ? cols - item.w : 0;
          }
          const gridItem = { i: `${idx}`, x: x, y: item.y, w: item.w, h: item.h, minH: 1, minW: 1, static: false };
          gridLayout.push(gridItem);
        }
      });
    }
    return { widgets, menus, layout: gridLayout };
  };

  const addNewWidget = (uuid) => {
    setAddMode(false);
    setEditMode(true);
    setSelectedUUID(uuid);
  };

  if (!gridMetrics) return null;
  if (!props.values || !props.values.widgets) return null;
  const { cols, gridWidth, rowHeight } = gridMetrics;
  const { values } = props;

  const widgets = renderWidgets(values?.widgets);

  const formActions = [
    {
      description: 'Add Widget',
      component: PrimaryButton,
      fn: addWidget,
      individualButton: true,
      actionReference: 'DASHBOARD_UPDATE',
      'data-testid': 'add-widget-button',
    },
  ];

  const possibleId = values.widgets.findIndex((widget) => {
    return widget.uuid === selectedUUID;
  });
  const selectedId = possibleId >= 0 ? possibleId : values.widgets.length; //Set to length so new widgets get a positive index.

  return (
    <ContentContainer>
      <FluentModal
        onClose={hideModal}
        isOpen={addMode || editMode}
        styles={{
          main: { minWidth: '80%', minHeight: '600px', paddingBottom: '60px' },
          scrollableContent: { overflowX: 'clip' },
        }}
      >
        <Form.Field
          dataid={`widgets.${selectedId}`}
          component={AddWidget}
          onClose={hideModal}
          dashboard={values}
          gridMetrics={gridMetrics}
          idx={'_editWidget'}
          addNewWidget={addNewWidget}
          app={app}
          formChange={props.blur}
        />
      </FluentModal>
      <Form.Header
        entityHdr="Dashboard"
        component={DashboardHeaderBlock}
        noDeleteOption={true}
        actionButtons={formActions}
        {...props}
      />

      <GridContainer>
        <GridLayout
          className="layout"
          onLayoutChange={updateLayout}
          layout={widgets.layout}
          cols={cols}
          rowHeight={rowHeight}
          width={gridWidth}
          autosize={false}
        >
          {widgets.widgets}
        </GridLayout>
      </GridContainer>
      {widgets.menus}
    </ContentContainer>
  );
};

export default compose(
  connect(mapStateToFormProps),
  withContainer.formQuery(entities.Dashboard.instance),
  withContainer.formMutation(entities.Dashboard.update),
  withContainer.defaultRecord(entities.Dashboard.default),
  createForm({
    form: entities.Dashboard.type,
    name: 'Dashboard',
    entity: entities.Dashboard,
    queryFragment: entities.Dashboard.fragment,
    defaultReturnUrl: '/',
    registeredFields: ['id', 'label', 'widgets.x', 'widgets.y', 'widgets.h', 'widgets.w'],
    getSubmitValues: (props) => {
      const values = cloneDeep(props.values);
      delete values.companyUser.systemUserId;
      return values;
    },
    shouldComponentUpdate: (props, nextProps) => {
      return props?.values?.widgets !== nextProps?.values?.widgets;
    },
  })
)(DashboardForm);
